import { ChorganiserLogo } from "@/components/logo";
import { LoadingOverlay } from "@/components/loading-overlay";
import { Button } from "@/components/ui/button";
import { authClient } from "@/lib/auth-client";
import { useMutation } from "@tanstack/react-query";
import { Navigate, useSearchParams } from "react-router-dom";

const ERROR_MESSAGES: Record<string, string> = {
  access_denied: "Access was denied. Please try again.",
  invalid_callback_request: "Something went wrong. Please try again.",
  invalid_code: "Authentication failed. Please try again.",
  oauth_provider_not_found: "Google sign-in is not configured.",
  unable_to_get_user_info: "Could not retrieve your account info. Please try again.",
};

function getErrorMessage(error: string | null, description: string | null) {
  if (!error) return null;
  return ERROR_MESSAGES[error] ?? description ?? "An unexpected error occurred. Please try again.";
}

export function LoginPage() {
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const [searchParams] = useSearchParams();
  const loginMutation = useMutation({
    mutationFn: async () => {
      const result = await authClient.signIn.social({
        provider: "google",
        callbackURL: "/chore-schedules",
        errorCallbackURL: "/login",
      });
      if (result.error) throw new Error(result.error.message ?? "Sign in failed");
    },
  });

  if (sessionPending) {
    return <LoadingOverlay isLoading />;
  }

  if (session) {
    return <Navigate to="/chore-schedules" replace />;
  }

  const oauthError = getErrorMessage(
    searchParams.get("error"),
    searchParams.get("error_description"),
  );
  const errorMessage = loginMutation.error?.message ?? oauthError;

  return (
    <LoadingOverlay isLoading={!loginMutation.isIdle && !loginMutation.isError}>
      <div className="min-h-dvh flex flex-col items-center justify-center bg-background px-6">
        <div className="w-full max-w-sm space-y-10">
          <div className="text-center space-y-2">
            <ChorganiserLogo className="-mb-10" />
            <h1 className="sr-only">Chorganiser</h1>
            <p className="text-muted-foreground">Organise and track your chores</p>
          </div>
          <div className="space-y-3">
            {errorMessage && <p className="text-sm text-destructive text-center">{errorMessage}</p>}
            <Button
              className="w-full gap-3"
              size="lg"
              variant="outline"
              onClick={() => loginMutation.mutate()}
              disabled={loginMutation.isPending}
            >
              <GoogleIcon />
              Sign in with Google
            </Button>
          </div>
        </div>
      </div>
    </LoadingOverlay>
  );
}

function GoogleIcon() {
  return <img src="/icons/google.svg" width="20px" height="20px" alt="Google Logo" />;
}
